import React, { lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Layout from './scaffolding/Layout';
import AuthorizedRoute from './authorization/AuthorizedRoute';
import { AuthenticationProvider } from './authorization/AuthenticationProvider';
import { useSelector, useDispatch } from 'react-redux';
import { Spinner, SuccessAlert, ErrorAlert } from '@optic-delight/ui-framework';
import './client/vault.css';
import ClientActions from './client/client-actions';
import { generateCodeVerifier, generateCodeChallengeFromVerifier } from './utilities/utilities';
import { authorizeAPI } from './services/services';
import PasscodeVerification from './client/PasscodeVerification';

const VaultDashboard = lazy(() => import('./client/VaultDashboard'));
const ManageClient = lazy(() => import('./client/ManageClient'));
const ManageScope = lazy(() => import('./scopes/ManageScope'));

const App = () => {
	const dispatch = useDispatch();
	const isFetchingData = useSelector(state => state.clientReducer.isFetchingData);
	const showAlert = useSelector(state => state.clientReducer.showAlert);
	const alertData = useSelector(state => state.clientReducer.alertData);
	const tokenGenerated = useSelector(state => state.clientReducer.tokenGenerated);

	useEffect(() => {
		if (showAlert) {
			window.scrollTo(0, 0);
		}
	}, [showAlert]);

	useEffect(() => {
		if (window && window.location && window.location.search) {
			const urlArray = window.location.href.split('?');
			const urlParams = window.location.search.replace('?', '');
			const redirectURL = urlArray[0];
			const tokenArray = urlParams.split('&');
			const authCode = tokenArray[0].replace('code=', '');
			if (redirectURL && authCode && sessionStorage.getItem('verifier')) {
				const details = {
					client_id: 'cas-vault-app',
					code_verifier: sessionStorage.getItem('verifier'),
					grant_type: 'authorization_code',
					code: authCode,
					redirect_uri: redirectURL
				};
				const encodedData = Object.keys(details)
					.map(key => encodeURIComponent(key) + '=' + encodeURIComponent(details[key]))
					.join('&');

				dispatch({ type: ClientActions.API_EXCHANGE_ACCESS_TOKEN, payload: encodedData });
			}
		} else {
			if (
				window &&
				window.location &&
				window.location.href &&
				!window.location.href.includes('passcodeVerification') &&
				sessionStorage.getItem('access_token') === null
			) {
				try {
					const verifier = generateCodeVerifier();
					sessionStorage.setItem('verifier', verifier);
					if (verifier) {
						generateCodeChallengeFromVerifier(verifier).then(challenge => {
							const data = {
								client_id: 'cas-vault-app',
								response_type: 'code',
								redirect_uri: encodeURIComponent(window.location.href),
								scope: 'cas:read cas:write cas:delete',
								code_challenge: challenge,
								code_challenge_method: 'S256'
							};
							window.location.href =
								authorizeAPI +
								'?' +
								'client_id=' +
								data.client_id +
								'&response_type=' +
								data.response_type +
								'&redirect_uri=' +
								data.redirect_uri +
								'&scope=' +
								data.scope +
								'&code_challenge=' +
								data.code_challenge +
								'&code_challenge_method=' +
								data.code_challenge_method;
						});
					}
				} catch (error) {
					// eslint-disable-next-line no-console
					console.error('-----error---');
					dispatch({
						type: ClientActions.APP_SHOW_ALERT,
						payload: { type: 'danger', message: 'Something went wrong' }
					});
				}
			}
		}
	}, [dispatch]);

	return (
		<React.Fragment>
			{isFetchingData && (
				<Spinner>
					<Spinner.Title />
				</Spinner>
			)}
			<div data-testid="App" className={isFetchingData ? 'loader_overlay' : ''}>
				{showAlert &&
					(alertData.type === 'success' ? (
						<SuccessAlert condensed messages={[alertData.message]} onClose={() => dispatch({ type: ClientActions.APP_HIDE_ALERT })} dismissible />
					) : (
						<ErrorAlert condensed messages={[alertData.message]} onClose={() => dispatch({ type: ClientActions.APP_HIDE_ALERT })} dismissible />
					))}
				<BrowserRouter>
					<Routes>
						<Route
							path="/passcodeVerification"
							element={
								<React.Suspense fallback={<>...</>}>
									<PasscodeVerification />
								</React.Suspense>
							}
						/>
					</Routes>
				</BrowserRouter>
				{tokenGenerated || sessionStorage.getItem('access_token') ? (
					<AuthenticationProvider>
						<BrowserRouter>
							<Routes>
								<Route path="/" element={<Layout />}>
									<Route
										index
										element={
											<AuthorizedRoute>
												<VaultDashboard />
											</AuthorizedRoute>
										}
									/>
									<Route
										path="/dashboard"
										element={
											<React.Suspense fallback={<>...</>}>
												<VaultDashboard />
											</React.Suspense>
										}
									/>
									<Route
										path="manageClient"
										element={
											<React.Suspense fallback={<>...</>}>
												<ManageClient />
											</React.Suspense>
										}
									/>
									<Route
										path="manageScope"
										element={
											<React.Suspense fallback={<>...</>}>
												<ManageScope />
											</React.Suspense>
										}
									/>
									<Route path="*" element={<NotFound />} />
								</Route>
							</Routes>
						</BrowserRouter>
					</AuthenticationProvider>
				) : (
					<Redirect />
				)}
			</div>
		</React.Fragment>
	);
};

function Redirect() {
	if (!window.location.href.includes('passcodeVerification')) {
		return <h4> Redirecting...</h4>;
	} else {
		return <></>;
	}
}

function NotFound() {
	if (!window.location.href.includes('passcodeVerification')) {
		return <>{'404: not found'}</>;
	} else {
		return <></>;
	}
}

export default App;
